import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  Bug,
  CheckCircle2,
  Clock,
  Layers,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";
import * as React from "react";

import type { LifecycleStateLabel } from "@/api/projects";
import { STATE_LABEL } from "@/components/runs/run-state-label";
import { cn } from "@/lib/utils";

/**
 * Signature component — the canonical run-state visualization across
 * tables, DAG nodes, headers, toasts, audit logs. If it's wrong here,
 * the whole product looks wrong.
 *
 * Behavior:
 * - cross-fade between states (280ms)
 * - emerald halo pulse on success-state transition (single shot, 600ms)
 * - 3px horizontal shake on failure transition (200ms)
 * - rotating arc on ``running`` / ``collecting_outputs`` / ``validating_outputs``
 * - dashed border + stack glyph on ``cached`` (visually distinct from
 *   fresh ``completed`` at a glance)
 * - sr-only span with full state text + reason for screen readers
 * - respects ``prefers-reduced-motion`` — pulse + shake skipped, but
 *   color + content always transitions (carries information)
 */

/**
 * The badge's PRESENTATION per state. The display TEXT moved out to
 * `run-state-label.ts` when VIS/V1's lifecycle rail became a second surface
 * that has to say a state out loud: the rail is a pure module and cannot pull
 * this file's framer-motion/lucide graph in, and two spellings of
 * `staging_inputs` drift apart silently. Colour and glyph stay here — they are
 * badge chrome, and nothing else renders them.
 */
const STATE_META: Record<
  LifecycleStateLabel,
  {
    bg: string;
    fg: string;
    border: string;
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  validating: {
    bg: "bg-state-validating-bg",
    fg: "text-state-validating-fg",
    border: "border-state-validating-border",
    icon: Clock,
  },
  cached: {
    bg: "bg-state-cached-bg",
    fg: "text-state-cached-fg",
    border: "border-state-cached-border border-dashed",
    icon: Layers,
  },
  staging_inputs: {
    bg: "bg-state-staging-bg",
    fg: "text-state-staging-fg",
    border: "border-state-staging-border",
    icon: Loader2,
  },
  dispatched: {
    bg: "bg-state-dispatched-bg",
    fg: "text-state-dispatched-fg",
    border: "border-state-dispatched-border",
    icon: Loader2,
  },
  running: {
    bg: "bg-state-running-bg",
    fg: "text-state-running-fg",
    border: "border-state-running-border",
    icon: Loader2,
  },
  collecting_outputs: {
    bg: "bg-state-collecting-bg",
    fg: "text-state-collecting-fg",
    border: "border-state-collecting-border",
    icon: Loader2,
  },
  validating_outputs: {
    bg: "bg-state-validating-outputs-bg",
    fg: "text-state-validating-outputs-fg",
    border: "border-state-validating-outputs-border",
    icon: Loader2,
  },
  completed: {
    bg: "bg-state-completed-bg",
    fg: "text-state-completed-fg",
    border: "border-state-completed-border",
    icon: CheckCircle2,
  },
  failed_validation: {
    bg: "bg-state-failed-validation-bg",
    fg: "text-state-failed-validation-fg",
    border: "border-state-failed-validation-border",
    icon: AlertTriangle,
  },
  failed_system: {
    bg: "bg-state-failed-system-bg",
    fg: "text-state-failed-system-fg",
    border: "border-state-failed-system-border",
    icon: RefreshCw,
  },
  failed_scientific: {
    bg: "bg-state-failed-scientific-bg",
    fg: "text-state-failed-scientific-fg",
    border: "border-state-failed-scientific-border",
    icon: XCircle,
  },
  failed_contract: {
    bg: "bg-state-failed-contract-bg",
    fg: "text-state-failed-contract-fg",
    border: "border-state-failed-contract-border",
    icon: Bug,
  },
};

const FAILURE_STATES = new Set<LifecycleStateLabel>([
  "failed_validation",
  "failed_system",
  "failed_scientific",
  "failed_contract",
]);

const SPINNING_STATES = new Set<LifecycleStateLabel>([
  "staging_inputs",
  "dispatched",
  "running",
  "collecting_outputs",
  "validating_outputs",
]);

export interface RunStateBadgeProps {
  state: LifecycleStateLabel;
  /** Free-form reason — surfaces to screen readers + sets the
   * tooltip body in callers that wrap with a Tooltip. */
  reason?: string;
  /** Optional size variant. ``sm`` is the default (table cells); ``md``
   * for headers; ``lg`` for the run-detail page hero. */
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<RunStateBadgeProps["size"]>, string> = {
  sm: "h-5 px-2 text-[11px] gap-1",
  md: "h-6 px-2.5 text-xs gap-1.5",
  lg: "h-7 px-3 text-sm gap-2",
};

const ICON_SIZE: Record<NonNullable<RunStateBadgeProps["size"]>, string> = {
  sm: "h-3 w-3",
  md: "h-3.5 w-3.5",
  lg: "h-4 w-4",
};

export function RunStateBadge({
  state,
  reason,
  size = "sm",
  className,
}: RunStateBadgeProps): JSX.Element {
  const meta = STATE_META[state];
  const label = STATE_LABEL[state];
  const Icon = meta.icon;
  const reduceMotion = useReducedMotion();
  const previousState = React.useRef(state);
  const [pulseKey, setPulseKey] = React.useState(0);
  const [shakeKey, setShakeKey] = React.useState(0);

  React.useEffect(() => {
    if (previousState.current === state) return;
    const wasNotCompleted = previousState.current !== "completed";
    const wasNotFailed = !FAILURE_STATES.has(previousState.current);
    if (state === "completed" && wasNotCompleted && !reduceMotion) {
      setPulseKey((k) => k + 1);
    } else if (FAILURE_STATES.has(state) && wasNotFailed && !reduceMotion) {
      setShakeKey((k) => k + 1);
    }
    previousState.current = state;
  }, [state, reduceMotion]);

  return (
    <span className="relative inline-flex">
      <motion.span
        key={shakeKey === 0 ? "stable" : `shake-${shakeKey}`}
        animate={shakeKey === 0 ? undefined : { x: [0, -3, 3, -3, 3, 0] }}
        transition={{ duration: 0.2 }}
        className={cn(
          "inline-flex items-center rounded-full border font-medium tabular-nums",
          SIZE_CLASSES[size],
          meta.bg,
          meta.fg,
          meta.border,
          "duration-[280ms] transition-colors",
          className
        )}
        aria-live="polite"
        aria-atomic="true"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={state}
            initial={reduceMotion ? false : { y: 4, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { y: -4, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="inline-flex items-center gap-[inherit]"
          >
            <Icon
              className={cn(
                ICON_SIZE[size],
                SPINNING_STATES.has(state) && !reduceMotion && "motion-safe:animate-arc-spin"
              )}
              aria-hidden="true"
            />
            <span>{label}</span>
          </motion.span>
        </AnimatePresence>
        <span className="sr-only">
          {label}
          {reason ? `: ${reason}` : ""}
        </span>
      </motion.span>
      {pulseKey > 0 && !reduceMotion && (
        <span
          key={`pulse-${pulseKey}`}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-full motion-safe:animate-state-pulse"
        />
      )}
    </span>
  );
}
